<template>
  <div class="dashboard-container">
    <!-- HEADER SECTION -->
    <b-card class="mb-4 dashboard-header" no-body>
      <b-card-body class="d-flex justify-content-between align-items-center py-3">
        <div>
          <div class="d-flex align-items-center">
            <div class="header-icon-wrapper mr-3">
              <b-icon-people class="header-icon" />
            </div>
            <div>
              <h5 class="mb-0 header-title">SALESPERSON PERFORMANCE</h5>
              <small class="header-subtitle">Track performance metrics and commission analytics</small>
            </div>
          </div>
        </div>
        <div class="d-flex align-items-center">
          <b-badge variant="light" class="mr-2 date-badge">
            <b-icon-calendar class="mr-1" />{{ formattedDateRange }}
          </b-badge>
          <b-badge variant="primary" pill class="animated-badge">
            {{ filteredItems.length }} {{ filteredItems.length === 1 ? 'Agent' : 'Agents' }}
          </b-badge>
        </div>
      </b-card-body>
    </b-card>

    <!-- FILTERS CARD -->
    <b-card class="mb-4 filters-card" no-body>
      <b-card-body class="py-3">
        <div class="row align-items-end">
          <div class="col-lg-8">
            <div class="d-flex flex-wrap align-items-end" style="gap: 16px;">
              <div style="min-width: 320px;">
                <label class="small text-muted mb-2 filter-label">
                  <b-icon-calendar class="mr-1" size="sm" />DATE RANGE
                </label>
                <date-picker
                  v-model="date_range"
                  format="MM-DD-YYYY"
                  valueType="format"
                  class="custom-datepicker"
                  placeholder="Select Date Range"
                  range
                  :class="{ 'datepicker-focused': dateRangeFocused }"
                  @focus="dateRangeFocused = true"
                  @blur="dateRangeFocused = false"
                ></date-picker>
              </div>

              <div class="d-flex flex-wrap" style="gap: 8px;">
                <b-button
                  v-for="btn in quickDateButtons"
                  :key="btn.id"
                  size="md"
                  :variant="picked_date_range === btn.id ? 'danger' : 'outline-danger'"
                  @click="set_dates(btn.id)"
                  class="quick-date-btn py-2 px-3"
                >
                  <b-icon v-if="picked_date_range === btn.id" :icon="btn.icon" class="mr-1" size="sm" />
                  {{ btn.label }}
                </b-button>
              </div>
            </div>
          </div>

          <div class="col-lg-4 mt-3 mt-lg-0">
            <div class="d-flex justify-content-end">
              <b-button
                variant="success"
                @click="fetch_init"
                class="search-btn"
                :disabled="items_being_loaded"
              >
                <b-icon-search class="mr-2" />
                <span v-if="!items_being_loaded">SEARCH</span>
                <span v-else>
                  <b-icon-arrow-repeat spin class="mr-2" />LOADING...
                </span>
              </b-button>
            </div>
          </div>
        </div>

        <!-- ADVANCED FILTERS -->
        <div class="mt-3">
          <b-button
            variant="link"
            size="md"
            @click="showAdvancedFilters = !showAdvancedFilters"
            class="p-0 text-decoration-none advanced-toggle"
          >
            <b-icon :icon="showAdvancedFilters ? 'chevron-up' : 'chevron-down'" class="mr-1" size="sm" />
            {{ showAdvancedFilters ? 'Hide' : 'Show' }} Filters
          </b-button>

          <b-collapse v-model="showAdvancedFilters">
            <div class="mt-3 pt-3 border-top">
              <div class="row">
                <div class="col-md-4 mb-3">
                  <label class="small text-muted mb-2 filter-label">
                    <b-icon-building class="mr-1" size="sm" />LOCATION
                  </label>
                  <b-form-select
                    :options="filteredLocations"
                    v-model="picked_location"
                    size="md"
                    class="filter-select"
                    @change="onFilterChange"
                  >
                    <option :value="null">All Locations</option>
                  </b-form-select>
                </div>

                <div class="col-md-4 mb-3">
                  <label class="small text-muted mb-2 filter-label">
                    <b-icon-person class="mr-1" size="sm" />SALESPERSON
                  </label>
                  <b-form-select
                    :options="filteredUsers"
                    v-model="picked_user"
                    size="md"
                    class="filter-select"
                    @change="onFilterChange"
                  >
                    <option :value="null">All Salespersons</option>
                  </b-form-select>
                </div>

                <div class="col-md-4 mb-3">
                  <label class="small text-muted mb-2 filter-label">
                    <b-icon-tag class="mr-1" size="sm" />SALES TYPE
                  </label>
                  <b-form-select
                    size="md"
                    :options="sales_types"
                    v-model="sales_type"
                    class="filter-select"
                    @change="onFilterChange"
                  ></b-form-select>
                </div>
              </div>
            </div>
          </b-collapse>
        </div>

        <!-- ACTIVE FILTERS DISPLAY -->
        <div class="mt-3 pt-3 border-top" v-if="selected_salesperson">
          <div class="d-flex flex-wrap align-items-center" style="gap: 12px;">
            <span class="small text-muted" style="font-size: 14px;">Active Filter:</span>
            <b-badge variant="primary" pill class="active-filter-badge">
              <b-icon-person class="mr-1" size="xs" />{{ selected_salesperson }}
              <b-icon-x class="ml-1" size="xs" @click="clearSalespersonFilter" style="cursor: pointer;" />
            </b-badge>
            <span class="small text-muted ml-2" style="font-size: 14px;">Showing data for selected salesperson only</span>
          </div>
        </div>
      </b-card-body>
    </b-card>

    <!-- LOADING STATE -->
    <div v-if="items_being_loaded" class="text-center py-5">
      <b-spinner variant="primary" type="grow" label="Loading..." style="width: 3rem; height: 3rem;"></b-spinner>
      <p class="mt-3 text-muted" style="font-size: 18px;">Fetching sales data...</p>
    </div>

    <!-- MAIN CONTENT -->
    <template v-else-if="filteredItems.length > 0">
      <!-- ============================================== -->
      <!-- KPI CARDS - REFERENCE STYLE (OPTION A)         -->
      <!-- ============================================== -->
      <div class="kpi-grid">
        <!-- Card 1: Gross Sales (DARK) -->
        <div class="kpi-card dark accent-green">
          <div class="kpi-icon-wrap icon-green">
            <b-icon-cash-stack />
          </div>
          <span class="kpi-label">Gross Sales</span>
          <span class="kpi-value">{{ formatCurrency(totalGrossSales) }}</span>
          <div class="kpi-trend trend-green">
            <b-icon-receipt />
            {{ totalNSales }} transactions
          </div>
        </div>

        <!-- Card 2: Net Sales -->
        <div class="kpi-card accent-blue">
          <div class="kpi-icon-wrap icon-blue">
            <b-icon-wallet />
          </div>
          <span class="kpi-label">Net Sales</span>
          <span class="kpi-value">{{ formatCurrency(totalNetSales) }}</span>
          <div class="kpi-trend trend-red">
            <b-icon-graph-down-arrow />
            Short: {{ formatCurrency(totalShortAmount) }}
          </div>
        </div>

        <!-- Card 3: Avg Sale -->
        <div class="kpi-card accent-amber">
          <div class="kpi-icon-wrap icon-amber">
            <b-icon-calculator />
          </div>
          <span class="kpi-label">Avg Sale</span>
          <span class="kpi-value">{{ formatCurrency(avgSaleValueOverall) }}</span>
          <div class="kpi-trend trend-amber">
            <b-icon-receipt />
            per transaction
          </div>
        </div>

        <!-- Card 4: Collection Rate -->
        <div class="kpi-card accent-purple">
          <div class="kpi-icon-wrap icon-purple">
            <b-icon-percent />
          </div>
          <span class="kpi-label">Collection Rate</span>
          <span class="kpi-value">{{ collectionRate }}%</span>
          <div class="kpi-trend trend-purple">
            <b-icon-cash />
            {{ formatCurrency(totalShortAmount) }} short
          </div>
        </div>
      </div>

      <!-- ============================================== -->
      <!-- COMPACT STATS ROW - SCALED UP                   -->
      <!-- ============================================== -->
      <div class="stat-row-grid">
        <div class="stat-mini-card accent-indigo">
          <div class="stat-mini-icon bg-indigo-light">
            <b-icon-receipt />
          </div>
          <div class="stat-mini-info">
            <span class="stat-mini-value">{{ totalDeliveries }}</span>
            <span class="stat-mini-label">Deliveries</span>
          </div>
        </div>
        <div class="stat-mini-card accent-emerald">
          <div class="stat-mini-icon bg-emerald-light">
            <b-icon-tools />
          </div>
          <div class="stat-mini-info">
            <span class="stat-mini-value">{{ totalSetups }}</span>
            <span class="stat-mini-label">Setups</span>
          </div>
        </div>
        <div class="stat-mini-card accent-amber">
          <div class="stat-mini-icon bg-amber-light">
            <b-icon-shield-check />
          </div>
          <div class="stat-mini-info">
            <span class="stat-mini-value">{{ totalWarranties }}</span>
            <span class="stat-mini-label">Warranties</span>
          </div>
        </div>
        <div class="stat-mini-card accent-red">
          <div class="stat-mini-icon bg-red-light">
            <b-icon-arrow-return-left />
          </div>
          <div class="stat-mini-info">
            <span class="stat-mini-value">{{ totalRefunds }}</span>
            <span class="stat-mini-label">Refunds</span>
          </div>
        </div>
      </div>

      <!-- DATA TABLE SECTION -->
      <b-card no-body class="mt-3 data-table-card">
        <b-card-header class="bg-light d-flex justify-content-between align-items-center py-2">
          <div class="d-flex align-items-center">
            <b-icon-table class="mr-2" size="sm" />
            <h6 class="mb-0 table-title">Salesperson Performance Details</h6>
            <b-badge variant="secondary" class="ml-2 table-badge">{{ filteredItems.length }} {{ filteredItems.length === 1 ? 'agent' : 'agents' }}</b-badge>
            <b-badge variant="light" class="ml-2 table-badge" v-if="!selected_salesperson">
              <b-icon-mouse2 size="sm" class="mr-1" />Click row to filter
            </b-badge>
            <b-badge variant="info" class="ml-2 table-badge" v-else>
              <b-icon-check-circle size="sm" class="mr-1" />Filtered by salesperson
            </b-badge>
          </div>
          <div>
            <b-button v-if="packet.userDetails.userrole == 'admin'" size="md" variant="outline-secondary" @click="exportToCSV" class="mr-2">
              <b-icon-download class="mr-1" size="sm" /> Export
            </b-button>
            <b-button size="md" variant="outline-primary" @click="fetch_init">
              <b-icon-arrow-repeat class="mr-1" size="sm" /> Refresh
            </b-button>
          </div>
        </b-card-header>
        <b-card-body class="p-0">
          <b-overlay :show="items_being_loaded" spinner-variant="primary" spinner-type="grow" spinner-small rounded="sm">
            <div v-if="filteredItems.length > 0" class="table-wrapper-sm">
              <table class="compact-table">
                <thead>
                  <tr>
                    <th @click="sortby('username')">AGENT</th>
                    <th @click="sortby('location_id')">LOCATION</th>
                    <th class="text-right" @click="sortby('n_sales')"># SALES</th>
                    <th class="text-right" @click="sortby('total_dollar_amount')">GROSS ($)</th>
                    <th class="text-right" @click="sortby('total_short_amount')">SHORT ($)</th>
                    <th class="text-right" @click="sortby('net_sales_amount')">NET ($)</th>
                    <th class="text-right" @click="sortby('average_sale_amount')">AVG ($)</th>
                    <th class="text-right">DELIVERIES</th>
                    <th class="text-right">SETUPS</th>
                    <th class="text-right">WARRANTIES</th>
                    <th class="text-right">REFUNDS</th>
                    <th v-if="packet.userDetails.userrole == 'admin'" class="text-right">COMM ($)</th>
                    <th class="text-right">SPIFF ($)</th>
                    <th class="text-right">WRNTY COMM ($)</th>
                    <th v-if="packet.userDetails.userrole == 'admin'" class="text-right">TOTAL COMM ($)</th>
                  </tr>
                </thead>
                <tbody>
                  <!-- TOTALS ROW -->
                  <tr class="totals-row">
                    <td colspan="2" class="text-right"><strong>TOTALS:</strong></td>
                    <td class="text-right"><strong>{{ totalNSales }}</strong></td>
                    <td class="text-right"><strong>{{ formatCurrency(totalGrossSales) }}</strong></td>
                    <td class="text-right text-danger"><strong>{{ formatCurrency(totalShortAmount) }}</strong></td>
                    <td class="text-right text-success"><strong>{{ formatCurrency(totalNetSales) }}</strong></td>
                    <td class="text-right"><strong>{{ formatCurrency(avgSaleValueOverall) }}</strong></td>
                    <td class="text-right"><strong>{{ totalDeliveries }}</strong></td>
                    <td class="text-right"><strong>{{ totalSetups }}</strong></td>
                    <td class="text-right"><strong>{{ totalWarranties }}</strong></td>
                    <td class="text-right"><strong>{{ totalRefunds }}</strong></td>
                    <td v-if="packet.userDetails.userrole == 'admin'" class="text-right text-primary"><strong>{{ formatCurrency(totalCommissionAll) }}</strong></td>
                    <td class="text-right"><strong>{{ formatCurrency(totalSpiffAll) }}</strong></td>
                    <td class="text-right"><strong>{{ formatCurrency(totalWarrantyCommAll) }}</strong></td>
                    <td v-if="packet.userDetails.userrole == 'admin'" class="text-right text-primary"><strong>{{ formatCurrency(totalCommissionAll + totalSpiffAll + totalWarrantyCommAll + totalDeliveriesCommAll + totalSetupsCommAll) }}</strong></td>
                  </tr>

                  <!-- DATA ROWS -->
                  <tr v-for="(item, idx) in paginatedItems" :key="idx"
                      :class="{
                        'top-performer-row': item.net_sales_amount > 10000,
                        'low-performer-row': item.net_sales_amount < 1000 && item.net_sales_amount > 0,
                        'highlight-row': selected_salesperson === item.username
                      }"
                      @click="selectSalespersonFromTable(item.username)">
                    <td>
                      <div class="agent-info">
                        <div class="agent-avatar" :style="{ backgroundColor: getAvatarColor(item.username) }">
                          {{ getInitials(item.username) }}
                        </div>
                        <strong>{{ item.username }}</strong>
                      </div>
                    </td>
                    <td>
                      <b-badge variant="light" class="location-badge">
                        {{ packet.locationsdict[item.location_id] || item.location_id }}
                      </b-badge>
                    </td>
                    <td class="text-right">{{ item.n_sales || 0 }}</td>
                    <td class="text-right">{{ formatCurrency(item.total_dollar_amount || 0) }}</td>
                    <td class="text-right text-danger font-weight-bold">{{ formatCurrency(item.total_short_amount || 0) }}</td>
                    <td class="text-right text-success font-weight-bold">{{ formatCurrency(item.net_sales_amount || 0) }}</td>
                    <td class="text-right">{{ formatCurrency(item.average_sale_amount || 0) }}</td>
                    <td class="text-right">{{ item.total_deliveries_sold || 0 }}</td>
                    <td class="text-right">{{ item.total_setups_sold || 0 }}</td>
                    <td class="text-right">{{ item.total_warranties_sold || 0 }}</td>
                    <td class="text-right text-danger">{{ item.total_refunds || 0 }}</td>
                    <td v-if="packet.userDetails.userrole == 'admin'" class="text-right">{{ formatCurrency(item.commission || 0) }}</td>
                    <td class="text-right">{{ formatCurrency(item.spiff_commission || 0) }}</td>
                    <td class="text-right">{{ formatCurrency(item.total_warranties_commission || 0) }}</td>
                    <td v-if="packet.userDetails.userrole == 'admin'" class="text-right font-weight-bold text-primary">
                      {{ formatCurrency((item.total_warranties_commission || 0) + (item.spiff_commission || 0) + (item.commission || 0) + (item.total_deliveries_commission || 0) + (item.total_setups_commission || 0)) }}
                    </td>
                  </tr>
                </tbody>
              </table>

              <!-- Pagination -->
              <div class="pagination-wrapper d-flex justify-content-between align-items-center p-3 bg-light">
                <small class="text-muted" style="font-size: 16px;">
                  Showing {{ (currentPage - 1) * pageSize + 1 }} to {{ Math.min(currentPage * pageSize, filteredItems.length) }} of {{ filteredItems.length }} agents
                </small>
                <b-pagination
                  v-model="currentPage"
                  :total-rows="filteredItems.length"
                  :per-page="pageSize"
                  size="md"
                  class="mb-0"
                  align="center"
                  @change="handlePageChange"
                ></b-pagination>
                <b-form-select v-model="pageSize" size="md" style="width: 85px;" @change="currentPage = 1">
                  <option :value="10">10</option>
                  <option :value="25">25</option>
                  <option :value="50">50</option>
                  <option :value="100">100</option>
                </b-form-select>
              </div>
            </div>
            <div v-else class="p-4 text-center">
              <b-alert show variant="light" class="mb-0" style="font-size: 18px;">
                <b-icon-inbox class="mr-2" />
                No sales data available. Select a date range and click SEARCH.
              </b-alert>
            </div>
          </b-overlay>
        </b-card-body>
      </b-card>
    </template>

    <!-- NO DATA STATE -->
    <div v-else-if="!items_being_loaded && filteredItems.length === 0 && items.length > 0" class="text-center py-5">
      <b-alert show variant="info" class="mb-0" style="font-size: 18px;">
        <b-icon-filter-circle class="mr-2" />
        No data matches the selected filters. Try adjusting your filters.
      </b-alert>
    </div>

    <div v-else-if="!items_being_loaded && items.length === 0" class="text-center py-5">
      <b-alert show variant="light" class="mb-0" style="font-size: 18px;">
        <b-icon-search class="mr-2" />
        Select a date range and click SEARCH to view salesperson performance
      </b-alert>
    </div>
  </div>
</template>

<script>
import DatePicker from 'vue2-datepicker';
import 'vue2-datepicker/index.css';
import moment from 'moment';
import 'moment/locale/en-gb';
import Requestor from '@/utilities/Requestor';

import {
  BIconPeople,
  BIconCalendar,
  BIconSearch,
  BIconCashStack,
  BIconWallet,
  BIconCalculator,
  BIconTable,
  BIconArrowRepeat,
  BIconDownload,
  BIconBuilding,
  BIconPerson,
  BIconTag,
  BIconCartCheck,
  BIconExclamationCircle,
  BIconGraphUp,
  BIconGraphDownArrow,
  BIconReceipt,
  BIconTools,
  BIconShieldCheck,
  BIconArrowReturnLeft,
  BIconInbox,
  BIconX,
  BIconMouse2,
  BIconCheckCircle,
  BIconPercent,
  BIconFilterCircle,
  BIconChevronUp,
  BIconChevronDown
} from 'bootstrap-vue';

export default {
  name: "ReportsSalespersons",
  components: {
    DatePicker,
    BIconPeople,
    BIconCalendar,
    BIconSearch,
    BIconCashStack,
    BIconWallet,
    BIconCalculator,
    BIconTable,
    BIconArrowRepeat,
    BIconDownload,
    BIconBuilding,
    BIconPerson,
    BIconTag,
    BIconCartCheck,
    BIconExclamationCircle,
    BIconGraphUp,
    BIconGraphDownArrow,
    BIconReceipt,
    BIconTools,
    BIconShieldCheck,
    BIconArrowReturnLeft,
    BIconInbox,
    BIconX,
    BIconMouse2,
    BIconCheckCircle,
    BIconPercent,
    BIconFilterCircle,
    BIconChevronUp,
    BIconChevronDown
  },
  data: function() {
    return {
      packet: {},
      items: [],
      picked_location: null,
      picked_user: null,
      selected_salesperson: null,
      items_being_loaded: false,
      date_range: [],
      picked_date_range: null,
      sales_types: [
        { value: null, text: "All Sales" },
        { value: "delivered", text: "Delivered Only" },
        { value: "written", text: "Written Only" },
      ],
      sales_type: null,
      sortKey: null,
      sortAsc: false,
      users: [],
      usersdict: {},
      dateRangeFocused: false,
      currentPage: 1,
      pageSize: 25,
      showAdvancedFilters: false,
      quickDateButtons: [
        { id: 'today', label: 'Today', icon: 'calendar-day' },
        { id: 'yesterday', label: 'Yesterday', icon: 'calendar-day' },
        { id: 'this_week', label: 'This Week', icon: 'calendar-week' },
        { id: 'last_week', label: 'Last Week', icon: 'calendar-week' },
        { id: 'this_month', label: 'This Month', icon: 'calendar-month' },
        { id: 'last_month', label: 'Last Month', icon: 'calendar-month' }
      ],
    }
  },
  computed: {
    formattedDateRange() {
      if (this.date_range && this.date_range.length === 2) {
        return `${this.date_range[0]} - ${this.date_range[1]}`;
      }
      return 'Select dates';
    },

    filteredItems() {
      let filtered = this.items.filter(item => this.allowed(item));

      if (this.selected_salesperson) {
        filtered = filtered.filter(item => item.username === this.selected_salesperson);
      }

      if (this.sortKey) {
        filtered = filtered.sort((a, b) => {
          let aVal = a[this.sortKey] || 0;
          let bVal = b[this.sortKey] || 0;
          return this.sortAsc ? aVal - bVal : bVal - aVal;
        });
      }

      return filtered;
    },

    paginatedItems() {
      const start = (this.currentPage - 1) * this.pageSize;
      const end = start + this.pageSize;
      return this.filteredItems.slice(start, end);
    },

    filteredUsers() {
      if (this.items.length === 0) return this.users;
      const usersWithData = new Set(this.items.map(item => item.username));
      return this.users.filter(user =>
        user.value === null || usersWithData.has(user.value)
      );
    },

    filteredLocations() {
      if (this.items.length === 0 || !this.packet.locations) return this.packet.locations || [];
      const locationsWithData = new Set(this.items.map(item => item.location_id));
      return this.packet.locations.filter(location =>
        location.value === null || locationsWithData.has(location.value)
      );
    },

    totalGrossSales() {
      return this.filteredItems.reduce((sum, item) => sum + (parseFloat(item.total_dollar_amount) || 0), 0);
    },

    totalShortAmount() {
      return this.filteredItems.reduce((sum, item) => sum + (parseFloat(item.total_short_amount) || 0), 0);
    },

    totalNetSales() {
      return this.filteredItems.reduce((sum, item) => sum + (parseFloat(item.net_sales_amount) || 0), 0);
    },

    totalNSales() {
      return this.filteredItems.reduce((sum, item) => sum + (parseInt(item.n_sales) || 0), 0);
    },

    totalDeliveries() {
      return this.filteredItems.reduce((sum, item) => sum + (parseInt(item.total_deliveries_sold) || 0), 0);
    },

    totalSetups() {
      return this.filteredItems.reduce((sum, item) => sum + (parseInt(item.total_setups_sold) || 0), 0);
    },

    totalWarranties() {
      return this.filteredItems.reduce((sum, item) => sum + (parseInt(item.total_warranties_sold) || 0), 0);
    },

    totalRefunds() {
      return this.filteredItems.reduce((sum, item) => sum + (parseInt(item.total_refunds) || 0), 0);
    },

    totalCommissionAll() {
      return this.filteredItems.reduce((sum, item) => sum + (parseFloat(item.commission) || 0), 0);
    },

    totalSpiffAll() {
      return this.filteredItems.reduce((sum, item) => sum + (parseFloat(item.spiff_commission) || 0), 0);
    },

    totalWarrantyCommAll() {
      return this.filteredItems.reduce((sum, item) => sum + (parseFloat(item.total_warranties_commission) || 0), 0);
    },

    totalDeliveriesCommAll() {
      return this.filteredItems.reduce((sum, item) => sum + (parseFloat(item.total_deliveries_commission) || 0), 0);
    },

    totalSetupsCommAll() {
      return this.filteredItems.reduce((sum, item) => sum + (parseFloat(item.total_setups_commission) || 0), 0);
    },

    avgSaleValue() {
      if (this.filteredItems.length === 0) return 0;
      return this.totalNetSales / this.filteredItems.length;
    },

    avgSaleValueOverall() {
      const totalSales = this.totalNSales;
      if (totalSales === 0) return 0;
      return this.totalNetSales / totalSales;
    },

    totalTransactions() {
      return this.totalNSales;
    },

    collectionRate() {
      const total = this.totalGrossSales;
      if (total === 0) return 0;
      const shortRate = (this.totalShortAmount / total) * 100;
      return Math.max(0, 100 - shortRate).toFixed(1);
    },

    commissionRate() {
      const total = this.totalNetSales;
      if (total === 0) return 0;
      return ((this.totalCommissionAll / total) * 100).toFixed(1);
    }
  },
  watch: {
    filteredItems() {
      this.currentPage = 1;
    },
    picked_user() {
      this.currentPage = 1;
      this.selected_salesperson = null;
    },
    picked_location() {
      this.currentPage = 1;
    }
  },
  methods: {
    formatCurrency(value) {
      if (value === undefined || value === null) return '0.00';
      return parseFloat(value).toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    },

    getInitials(name) {
      if (!name) return '?';
      return name.charAt(0).toUpperCase();
    },

    getAvatarColor(name) {
      const colors = ['#6366f1', '#10b981', '#f59e0b', '#ef4444', '#06b6d4', '#8b5cf6', '#ec4899'];
      let hash = 0;
      for (let i = 0; i < name.length; i++) {
        hash = ((hash << 5) - hash) + name.charCodeAt(i);
        hash |= 0;
      }
      return colors[Math.abs(hash) % colors.length];
    },

    allowed: function(item) {
      if (this.picked_user == null && this.picked_location == null) {
        return true;
      }
      if (this.picked_user != null && this.picked_location == null) {
        return item.username == this.picked_user;
      }
      if (this.picked_user == null && this.picked_location != null) {
        return item.location_id == this.picked_location;
      }
      if (this.picked_user != null && this.picked_location != null) {
        return item.username == this.picked_user && item.location_id == this.picked_location;
      }
      return true;
    },

    selectSalespersonFromTable(salesperson) {
      if (this.selected_salesperson === salesperson) {
        this.selected_salesperson = null;
      } else {
        this.selected_salesperson = salesperson;
        if (this.picked_user === salesperson) {
          this.picked_user = null;
        }
      }
      this.currentPage = 1;
    },

    clearSalespersonFilter() {
      this.selected_salesperson = null;
      this.currentPage = 1;
    },

    onFilterChange() {
      this.selected_salesperson = null;
      this.currentPage = 1;
    },

    set_dates: function(value) {
      let start = null;
      let end = null;

      const formats = {
        today: () => {
          start = moment().format("MM-DD-YYYY");
          end = moment().format("MM-DD-YYYY");
        },
        yesterday: () => {
          start = moment().add(-1, 'days').format("MM-DD-YYYY");
          end = moment().add(-1, 'days').format("MM-DD-YYYY");
        },
        this_week: () => {
          start = moment().startOf('week').format("MM-DD-YYYY");
          end = moment().endOf('week').format("MM-DD-YYYY");
        },
        last_week: () => {
          start = moment().subtract(1, 'weeks').startOf('week').format("MM-DD-YYYY");
          end = moment().subtract(1, 'weeks').endOf('week').format("MM-DD-YYYY");
        },
        this_month: () => {
          start = moment().startOf('month').format("MM-DD-YYYY");
          end = moment().endOf('month').format("MM-DD-YYYY");
        },
        last_month: () => {
          start = moment().subtract(1, 'months').startOf('month').format("MM-DD-YYYY");
          end = moment().subtract(1, 'months').endOf('month').format("MM-DD-YYYY");
        }
      };

      if (formats[value]) {
        formats[value]();
        if (start && end) {
          this.date_range = [start, end];
          this.picked_date_range = value;
          this.fetch_init();
        }
      }
    },

    fetch_init: function() {
      let dates = this.date_range;
      let that = this;
      let data = {
        dates: dates,
        username: this.packet.userDetails.username,
        sales_type: this.sales_type,
        location_id: this.picked_location,
        salesperson: this.picked_user
      };
      let path = "/api/reports/reports_v1/";

      let s = function(response) {
        if (response.data.result) {
          that.items = response.data.result;
          that.selected_salesperson = null;
          that.$bvToast.toast(
            `Loaded ${that.items.length} salesperson records`,
            { title: "Success", autoHideDelay: 1500, variant: "success" }
          );
        } else {
          that.$bvToast.toast(
            response.data.error || "No data returned",
            { title: "Info", autoHideDelay: 1500, variant: "info" }
          );
        }
        that.items_being_loaded = false;
      };

      let f = function(error) {
        console.log(error);
        that.$bvToast.toast(
          "Error occurred. Please try again.",
          { title: "Error", autoHideDelay: 1500, variant: "danger" }
        );
        that.items_being_loaded = false;
      };

      this.items_being_loaded = true;
      Requestor.postSyncP(path, data, this, s, f);
    },

    sortby: function(key) {
      if (this.sortKey === key) {
        this.sortAsc = !this.sortAsc;
      } else {
        this.sortKey = key;
        this.sortAsc = true;
      }
    },

    handlePageChange(page) {
      this.currentPage = page;
    },

    exportToCSV() {
      const headers = [
        'Agent', 'Location', '# Sales', 'Gross ($)', 'Short Amount ($)',
        'Net ($)', '# Short Pmt', 'Avg Sale ($)', 'Total Sales Tax ($)',
        '# Delvrs', 'Total Delvrs ($)', '# Setups', 'Total Setups ($)',
        '# Warrants', 'Total Warrants ($)', '# Refunds', 'Total Refunds ($)',
        'Comm ($)', 'Spiff ($)', 'Warranty Comm ($)', 'Total Comm ($)'
      ];

      const rows = this.filteredItems.map(item => [
        item.username,
        this.packet.locationsdict[item.location_id] || item.location_id,
        item.n_sales || 0,
        (parseFloat(item.total_dollar_amount) || 0).toFixed(2),
        (parseFloat(item.total_short_amount) || 0).toFixed(2),
        (parseFloat(item.net_sales_amount) || 0).toFixed(2),
        item.n_short_payments || 0,
        (parseFloat(item.average_sale_amount) || 0).toFixed(2),
        (parseFloat(item.total_sales_tax) || 0).toFixed(2),
        item.total_deliveries_sold || 0,
        (parseFloat(item.total_deliveries_value) || 0).toFixed(2),
        item.total_setups_sold || 0,
        (parseFloat(item.total_setups_value) || 0).toFixed(2),
        item.total_warranties_sold || 0,
        (parseFloat(item.total_warranties_value) || 0).toFixed(2),
        item.total_refunds || 0,
        (parseFloat(item.total_refunds_value) || 0).toFixed(2),
        (parseFloat(item.commission) || 0).toFixed(2),
        (parseFloat(item.spiff_commission) || 0).toFixed(2),
        (parseFloat(item.total_warranties_commission) || 0).toFixed(2),
        ((parseFloat(item.total_warranties_commission) || 0) +
         (parseFloat(item.spiff_commission) || 0) +
         (parseFloat(item.commission) || 0) +
         (parseFloat(item.total_deliveries_commission) || 0) +
         (parseFloat(item.total_setups_commission) || 0)).toFixed(2)
      ]);

      const csvContent = [headers, ...rows].map(row => row.join(',')).join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `salesperson_report_${moment().format('YYYY-MM-DD')}.csv`;
      a.click();
      URL.revokeObjectURL(url);

      this.$bvToast.toast('Export completed!', { title: 'Success', autoHideDelay: 1500, variant: 'success' });
    },

    fetch_users_list: function() {
      let data = { userrole: this.packet.userDetails.userrole, user_id: this.packet.userDetails.id };
      let that = this;
      let path = "/api/user/get_my_users_list/";

      let s = function(response) {
        if (response.data.result) {
          that.users = response.data.result;
          that.users.splice(0, 0, { value: null, text: "All Salespersons" });
          for (var i = 0; i < that.users.length; i++) {
            that.usersdict[that.users[i].value] = true;
          }
        } else {
          that.$bvToast.toast(
            response.data.error || "Error loading users",
            { title: "Error", autoHideDelay: 1500, variant: "warning" }
          );
        }
      };

      let f = function(error) {
        console.log(error);
        that.$bvToast.toast(
          "Error occurred loading users.",
          { title: "Error", autoHideDelay: 1500, variant: "danger" }
        );
      };

      Requestor.getAsyncP(path, data, this, s, f);
    },
  },
  created: function() {
    this.packet = JSON.parse(localStorage.packet);
    this.packet.userDetails = JSON.parse(localStorage.userDetails);

    const start = moment().startOf('month').format("MM-DD-YYYY");
    const end = moment().endOf('month').format("MM-DD-YYYY");
    this.date_range = [start, end];
    this.picked_date_range = 'this_month';

    if (this.packet.userDetails.userrole == "admin" || this.packet.userDetails.userrole == "locationadmin") {
      this.fetch_users_list();
    }

    this.fetch_init();
  }
}
</script>

<style scoped>
/* =========================================================
   GLOBAL
   ========================================================= */
.dashboard-container {
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  min-height: 100vh;
  padding: 24px;
}

/* =========================================================
   HEADER
   ========================================================= */
.dashboard-header {
  background: linear-gradient(135deg, #1e293b 0%, #0f172a 100%);
  color: white;
  border: none;
  border-radius: 18px;
  box-shadow: 0 6px 24px rgba(0, 0, 0, 0.15);
}

.header-icon-wrapper {
  width: 56px;
  height: 56px;
  background: rgba(255, 255, 255, 0.15);
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.header-icon {
  font-size: 28px;
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.08); }
}

.header-title {
  font-size: 32px;
  font-weight: 700;
  letter-spacing: -0.5px;
}

.header-subtitle {
  font-size: 16px;
  opacity: 0.8;
}

.date-badge {
  background-color: rgba(255, 255, 255, 0.15);
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 500;
}

.animated-badge {
  animation: fadeIn 0.5s ease-out;
  font-size: 14px;
  padding: 8px 16px;
}

/* =========================================================
   FILTERS
   ========================================================= */
.filters-card {
  border: none;
  border-radius: 18px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
  background: white;
}

.filter-label {
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0.5px;
}

.custom-datepicker {
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 10px 16px;
  font-size: 18px;
  width: 100%;
}

.datepicker-focused {
  border-color: #6366f1;
  box-shadow: 0 0 0 4px rgba(99, 102, 241, 0.15);
}

.quick-date-btn {
  border-radius: 20px;
  font-size: 16px;
  padding: 10px 18px;
  font-weight: 500;
}

.filter-select {
  border-radius: 12px;
  background: #f8fafc;
  border-color: #e2e8f0;
  font-size: 18px;
  padding: 10px 16px;
}

.search-btn {
  border-radius: 12px;
  padding: 12px 36px;
  font-weight: 700;
  font-size: 18px;
  letter-spacing: 0.5px;
}

.advanced-toggle {
  font-size: 16px;
  font-weight: 500;
}

.active-filter-badge {
  cursor: default;
  background: #6366f1;
  color: #fff;
  font-size: 14px;
  padding: 8px 16px;
}

/* =========================================================
   KPI CARDS - REFERENCE STYLE (OPTION A)
   ========================================================= */
.kpi-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 24px;
  margin-bottom: 24px;
}

.kpi-card {
  background: #fff;
  border-radius: 20px;
  padding: 36px 24px 30px;
  border: 1px solid #e8edf3;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  transition: all 0.2s ease;
  box-shadow: 0 4px 14px rgba(15, 23, 42, 0.06);
  position: relative;
  overflow: hidden;
}

.kpi-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 6px;
  background: linear-gradient(90deg, #6366f1, #8b5cf6);
}

.kpi-card:hover {
  box-shadow: 0 10px 28px rgba(15, 23, 42, 0.12);
  transform: translateY(-2px);
}

.kpi-card.dark {
  background: #0b1220;
  border-color: #0b1220;
  box-shadow: 0 8px 26px rgba(11, 18, 32, 0.35);
}

.kpi-card.dark .kpi-label {
  color: rgba(255, 255, 255, 0.65);
}

.kpi-card.dark .kpi-value {
  color: #ffffff;
}

/* Accent stripe colors */
.accent-green::before   { background: linear-gradient(90deg, #10b981, #34d399); }
.accent-blue::before    { background: linear-gradient(90deg, #3b82f6, #60a5fa); }
.accent-amber::before   { background: linear-gradient(90deg, #f59e0b, #fbbf24); }
.accent-purple::before  { background: linear-gradient(90deg, #8b5cf6, #a78bfa); }
.accent-indigo::before  { background: linear-gradient(90deg, #6366f1, #818cf8); }
.accent-emerald::before { background: linear-gradient(90deg, #10b981, #34d399); }
.accent-red::before     { background: linear-gradient(90deg, #ef4444, #f87171); }

/* Icon box */
.kpi-icon-wrap {
  width: 76px;
  height: 76px;
  border-radius: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 34px;
  margin-bottom: 22px;
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.12);
}

.icon-green   { background: linear-gradient(135deg, #10b981, #34d399); }
.icon-blue    { background: linear-gradient(135deg, #3b82f6, #60a5fa); }
.icon-amber   { background: linear-gradient(135deg, #f59e0b, #fbbf24); }
.icon-purple  { background: linear-gradient(135deg, #8b5cf6, #a78bfa); }

/* Label */
.kpi-label {
  font-size: 18px;
  color: #64748b;
  font-weight: 500;
  margin-bottom: 12px;
  letter-spacing: 0.2px;
}

/* Big value */
.kpi-value {
  font-size: 46px;
  font-weight: 800;
  color: #0b1220;
  line-height: 1.1;
  letter-spacing: -1.5px;
  margin-bottom: 18px;
  word-break: break-word;
}

/* Trend pill badge */
.kpi-trend {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 15px;
  font-weight: 600;
  padding: 8px 18px;
  border-radius: 999px;
  white-space: nowrap;
}

.trend-green   { color: #047857; background: #d1fae5; }
.trend-red     { color: #b91c1c; background: #fee2e2; }
.trend-purple  { color: #6d28d9; background: #ede9fe; }
.trend-amber   { color: #92400e; background: #fef3c7; }

.kpi-card.dark .kpi-trend.trend-green {
  color: #34d399;
  background: rgba(16, 185, 129, 0.18);
}

/* =========================================================
   COMPACT STATS ROW - SCALED UP
   ========================================================= */
.stat-row-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;
  margin-bottom: 24px;
}

.stat-mini-card {
  background: #fff;
  border-radius: 16px;
  padding: 22px 24px;
  border: 1px solid #e8edf3;
  display: flex;
  align-items: center;
  gap: 18px;
  box-shadow: 0 4px 14px rgba(15, 23, 42, 0.06);
  transition: all 0.2s ease;
  position: relative;
  overflow: hidden;
}

.stat-mini-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 5px;
  background: linear-gradient(90deg, #6366f1, #8b5cf6);
}

.stat-mini-card:hover {
  box-shadow: 0 10px 26px rgba(15, 23, 42, 0.1);
  transform: translateY(-2px);
}

.stat-mini-icon {
  width: 64px;
  height: 64px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 28px;
  flex-shrink: 0;
}

.bg-indigo-light {
  background: rgba(99, 102, 241, 0.12);
  color: #6366f1;
}

.bg-emerald-light {
  background: rgba(16, 185, 129, 0.12);
  color: #10b981;
}

.bg-amber-light {
  background: rgba(245, 158, 11, 0.12);
  color: #f59e0b;
}

.bg-red-light {
  background: rgba(239, 68, 68, 0.12);
  color: #ef4444;
}

.stat-mini-info {
  display: flex;
  flex-direction: column;
}

.stat-mini-value {
  font-size: 32px;
  font-weight: 800;
  color: #0b1220;
  line-height: 1.1;
  letter-spacing: -0.5px;
}

.stat-mini-label {
  font-size: 15px;
  color: #64748b;
  font-weight: 500;
  margin-top: 2px;
}

/* =========================================================
   DATA TABLE
   ========================================================= */
.data-table-card {
  border: none;
  border-radius: 18px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
  overflow: hidden;
}

.table-title {
  font-size: 20px;
  font-weight: 600;
}

.table-badge {
  font-size: 14px;
  padding: 6px 12px;
}

.table-wrapper-sm {
  overflow-x: auto;
}

.compact-table {
  width: 100%;
  font-size: 16px;
  border-collapse: collapse;
}

.compact-table th,
.compact-table td {
  padding: 14px 16px;
  border: 1px solid #dee2e6;
  font-size: 16px;
}

.compact-table th {
  background: #c3cdd3;
  font-weight: 700;
  position: sticky;
  top: 0;
  white-space: nowrap;
  cursor: pointer;
  font-size: 16px;
  text-transform: uppercase;
  letter-spacing: 0.4px;
}

.compact-table td {
  white-space: nowrap;
}

.compact-table tbody tr {
  cursor: pointer;
  transition: background 0.2s;
}

.compact-table tbody tr:hover {
  background: #e0e7ff !important;
}

.compact-table tbody tr.highlight-row {
  background: #dbeafe !important;
  border-left: 5px solid #6366f1;
}

.totals-row {
  background: #fef3c7;
  font-weight: 600;
}

.top-performer-row {
  background: linear-gradient(90deg, rgba(16, 185, 129, 0.05), transparent);
}

.low-performer-row {
  background: linear-gradient(90deg, rgba(239, 68, 68, 0.05), transparent);
}

/* Agent Info */
.agent-info {
  display: flex;
  align-items: center;
  gap: 12px;
}

.agent-avatar {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  color: white;
  font-size: 18px;
  flex-shrink: 0;
}

.location-badge {
  font-size: 14px;
  padding: 4px 12px;
  border-radius: 12px;
}

.pagination-wrapper {
  border-top: 1px solid #e2e8f0;
}

/* Animations */
@keyframes fadeIn {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}

.kpi-card, .stat-mini-card, .data-table-card, .filters-card {
  animation: fadeIn 0.5s ease-out;
}

/* Text colors */
.text-danger { color: #dc3545 !important; }
.text-success { color: #28a745 !important; }
.font-weight-bold { font-weight: bold !important; }
.border-top { border-top: 1px solid #e2e8f0; }
.text-right { text-align: right !important; }
.text-primary { color: #6366f1 !important; }

/* =========================================================
   RESPONSIVE
   ========================================================= */
@media (max-width: 1400px) {
  .kpi-grid { grid-template-columns: repeat(2, 1fr); }
  .stat-row-grid { grid-template-columns: repeat(4, 1fr); }
}

@media (max-width: 992px) {
  .dashboard-container { padding: 16px; }
  .header-title { font-size: 24px; }
  .kpi-grid { grid-template-columns: repeat(2, 1fr); gap: 16px; }
  .kpi-value { font-size: 36px; }
  .kpi-icon-wrap { width: 64px; height: 64px; font-size: 28px; margin-bottom: 16px; }
  .kpi-card { padding: 28px 18px 22px; }
  .stat-row-grid { grid-template-columns: repeat(2, 1fr); gap: 16px; }
  .stat-mini-value { font-size: 26px; }
  .stat-mini-icon { width: 52px; height: 52px; font-size: 22px; }
  .compact-table th, .compact-table td { padding: 10px 12px; font-size: 14px; }
  .table-title { font-size: 16px; }
}

@media (max-width: 576px) {
  .dashboard-container { padding: 10px; }
  .kpi-grid { grid-template-columns: 1fr; }
  .kpi-value { font-size: 32px; }
  .kpi-label { font-size: 15px; }
  .kpi-trend { font-size: 13px; padding: 6px 12px; }
  .stat-row-grid { grid-template-columns: 1fr; }
  .stat-mini-value { font-size: 22px; }
  .stat-mini-icon { width: 44px; height: 44px; font-size: 20px; }
  .compact-table th, .compact-table td { padding: 8px 10px; font-size: 12px; }
  .dashboard-header h5 { font-size: 18px; }
  .header-subtitle { font-size: 12px; }
  .agent-avatar { width: 32px; height: 32px; font-size: 14px; }
  .agent-info { gap: 8px; }
}
</style>


// WEBPACK FOOTER //
// src/components/Reports/ReportsReportsV1.vue